"use client";

import { cn } from "@repo/ui";
import { HexagonBackground } from "@repo/ui/components/shadcn/hexagon-background";
import * as React from "react";

const DEFAULT_HEXAGON_SIZE = 50;
const DEFAULT_HEXAGON_MARGIN = 3;
const DEFAULT_PATTERN_IMAGE_URL = "/images/landing-page/hextile.png";
const DEFAULT_PATTERN_LAYER_CLASS = "opacity-8 dark:invert dark:opacity-[0.06]";

export interface InteractiveHexagonBackgroundProps {
	/** Accessible name for the interactive hit surface. */
	interactiveAriaLabel: string;
	children: React.ReactNode;
	/** Classes on the interactive hit surface (`relative flex min-h-0 flex-1 flex-col select-none` base). */
	className?: string;
	/** Classes on the `pointer-events-none` content column wrapper. */
	contentWrapperClassName?: string;
	/** Optional layers rendered after the main content column (e.g. hero corner screenshots). */
	overlays?: React.ReactNode;
	/** Background behind the hex grid (e.g. `{ background: "var(--marketing-hero-fill)" }`). */
	backgroundStyle?: React.CSSProperties;
	/** Optional class overrides for the hextile wash layer. */
	patternLayerClassName?: string;
	/** Override the hextile pattern image URL. */
	patternImageUrl?: string;
	/** Customize hex sizing (advanced). */
	hexagonSize?: number;
	hexagonMargin?: number;
}

/**
 * Interactive hex grid + hextile wash background.
 *
 * Shared across marketing and SaaS surfaces (not coupled to waitlist/marketing).
 */
export function InteractiveHexagonBackground({
	interactiveAriaLabel,
	children,
	className,
	contentWrapperClassName = "justify-center py-4 sm:py-6",
	overlays,
	backgroundStyle,
	patternLayerClassName = DEFAULT_PATTERN_LAYER_CLASS,
	patternImageUrl = DEFAULT_PATTERN_IMAGE_URL,
	hexagonSize = DEFAULT_HEXAGON_SIZE,
	hexagonMargin = DEFAULT_HEXAGON_MARGIN,
}: InteractiveHexagonBackgroundProps) {
	const hitAreaRef = React.useRef<HTMLElement | null>(null);
	const [hoveredHexagon, setHoveredHexagon] = React.useState<{
		row: number;
		col: number;
	} | null>(null);
	const [selectedHexagon, setSelectedHexagon] = React.useState<{
		row: number;
		col: number;
	} | null>(null);
	const [isContentReady, setIsContentReady] = React.useState(false);
	const [showHexagonBackground, setShowHexagonBackground] =
		React.useState(false);

	const hexagonCalculations = React.useMemo(() => {
		const hexagonWidth = hexagonSize;
		const hexagonHeight = hexagonSize * 1.1;
		const rowSpacing = hexagonSize * 0.8;
		const baseMarginTop = -36 - 0.275 * (hexagonSize - 100);
		const computedMarginTop = baseMarginTop + hexagonMargin;
		const oddRowMarginLeft = -(hexagonSize / 2);
		const evenRowMarginLeft = hexagonMargin / 2;
		return {
			hexagonWidth,
			hexagonHeight,
			rowSpacing,
			computedMarginTop,
			oddRowMarginLeft,
			evenRowMarginLeft,
		};
	}, [hexagonMargin, hexagonSize]);

	const handleMouseMoveInternal = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (!hitAreaRef.current) {
				return;
			}

			const rect = hitAreaRef.current.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			const row0Center =
				hexagonCalculations.computedMarginTop +
				hexagonCalculations.hexagonHeight / 2;
			const adjustedY = y - row0Center;
			const actualSpacing = hexagonCalculations.rowSpacing * 1.075;
			const row = Math.round(adjustedY / actualSpacing);

			if (row < 0) {
				setHoveredHexagon(null);
				return;
			}

			const isEvenRow1Indexed = (row + 1) % 2 === 0;
			const rowLeftOffset = isEvenRow1Indexed
				? hexagonCalculations.evenRowMarginLeft - 10
				: hexagonCalculations.oddRowMarginLeft - 10;

			const adjustedX =
				x -
				rowLeftOffset -
				hexagonMargin -
				hexagonCalculations.hexagonWidth / 2;

			if (adjustedX < -hexagonCalculations.hexagonWidth / 2) {
				setHoveredHexagon(null);
				return;
			}

			let col = Math.round(
				adjustedX / (hexagonCalculations.hexagonWidth + hexagonMargin),
			);
			if (col < 0) {
				col = 0;
			}

			setHoveredHexagon({ row, col });
		},
		[hexagonCalculations, hexagonMargin],
	);

	const lastMouseMoveTime = React.useRef(0);
	const handleMouseMove = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			const now = performance.now();
			if (now - lastMouseMoveTime.current >= 16) {
				handleMouseMoveInternal(e);
				lastMouseMoveTime.current = now;
			}
		},
		[handleMouseMoveInternal],
	);

	const handleMouseLeave = React.useCallback(() => {
		setHoveredHexagon(null);
	}, []);

	const handleMouseDown = React.useCallback(() => {
		if (hoveredHexagon) {
			setSelectedHexagon(hoveredHexagon);
		}
	}, [hoveredHexagon]);

	React.useEffect(() => {
		setIsContentReady(true);
		const timer = window.setTimeout(() => {
			setShowHexagonBackground(true);
		}, 50);
		return () => window.clearTimeout(timer);
	}, []);

	return (
		<section
			ref={hitAreaRef}
			className={cn(
				"relative flex min-h-0 flex-1 flex-col select-none",
				className,
			)}
			style={backgroundStyle}
			aria-label={interactiveAriaLabel}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			onMouseDown={handleMouseDown}
			onKeyDown={() => {
				// Pointer-first interaction; keep focusable without changing behavior.
			}}
		>
			{showHexagonBackground ? (
				<div
					className="absolute inset-0 group z-0 opacity-80 transition-opacity duration-700 ease-out"
					style={{ opacity: isContentReady ? 0.8 : 0 }}
				>
					<HexagonBackground
						hexagonSize={hexagonSize}
						hexagonMargin={hexagonMargin}
						className="h-full w-full"
						hoveredHexagon={hoveredHexagon}
						selectedHexagon={selectedHexagon}
						isTranslucent={false}
						isClickable
					/>
				</div>
			) : null}

			<div
				className={cn("absolute inset-0 z-0", patternLayerClassName)}
				style={{
					backgroundImage: `url(${patternImageUrl})`,
					backgroundRepeat: "repeat",
					backgroundSize: "6px",
				}}
			/>

			<div
				className={cn(
					"pointer-events-none relative z-10 flex min-h-0 flex-1 flex-col",
					contentWrapperClassName,
				)}
			>
				<div className="pointer-events-auto w-full">{children}</div>
			</div>

			{overlays}
		</section>
	);
}
